export const tradeShowProductionPage = {
  site: 'miamisoundrental',
  path: '/trade-show-av-services',
  aliases: [],
  canonicalUrl: 'https://miamisoundrental.com/trade-show-av-services',
  locale: 'en-US',
  location: 'global',
  routeType: 'static',
  templateKey: 'trade-show-production-v1',
  indexPolicy: 'index'
}

export const tradeShowProductionRevision = {
  schemaVersion: 1,
  seo: {
    title: 'Trade Show & Expo AV Services in Florida | MSR',
    description: 'Trade show AV services for exhibitor booths, sponsor activations, and expo floors across Florida. LED, displays, audio, lighting, install, support, and dismantle.',
    canonicalUrl: 'https://miamisoundrental.com/trade-show-av-services',
    robots: {
      index: true,
      follow: true
    },
    openGraph: {
      type: 'website',
      url: 'https://miamisoundrental.com/trade-show-av-services',
      title: 'Trade Show & Expo AV Services in Florida | MSR',
      description: 'One accountable AV team for exhibitor booths, brand activations, and show-floor installations across Florida.',
      images: [{
        url: 'https://miamisoundrental.com/Pictures/trade-show/louisiana-booth-led-display.webp',
        width: 1600,
        height: 1200,
        alt: 'Large LED video display integrated into a Louisiana tourism trade show booth'
      }]
    },
    twitter: {
      title: 'Trade Show & Expo AV Services in Florida | MSR',
      description: 'LED, displays, audio, lighting, install, show support, and dismantle for trade shows and expos across Florida.',
      cardType: 'summary_large_image',
      images: [{
        url: 'https://miamisoundrental.com/Pictures/trade-show/louisiana-booth-led-display.webp',
        width: 1600,
        height: 1200,
        alt: 'Large LED video display integrated into a Louisiana tourism trade show booth'
      }]
    },
    keywords: [
      'trade show AV services',
      'trade show equipment rental',
      'trade show production',
      'trade show audio visual',
      'trade show production company',
      'trade show booth AV',
      'exhibitor AV services',
      'expo AV services',
      'exhibition AV services'
    ],
    primaryKeyword: 'trade show AV services',
    secondaryKeywords: [
      'trade show equipment rental',
      'trade show production',
      'trade show audio visual',
      'trade show production company',
      'trade show booth AV',
      'exhibitor AV services',
      'expo AV services',
      'exhibition AV services'
    ]
  },
  content: {
    h1: 'Trade Show & Expo AV Services in Florida',
    custom: {
      tradeShowProduction: {
        hero: {
          eyebrow: 'Trade show & expo AV · Florida',
          title: 'Trade Show & Expo AV Services in Florida',
          description: 'A Florida trade show production company for booth displays, LED video walls, audio, lighting, content playback, installation, show support, and dismantle—from exhibitor brief through strike.',
          image: '/Pictures/trade-show/louisiana-booth-led-display.webp',
          imageAlt: 'Large LED video display integrated into a Louisiana tourism trade show booth',
          imagePosition: 'center 48%',
          primaryAction: 'Request a Trade Show AV Proposal',
          secondaryAction: 'Call an Expo Producer'
        },
        proof: [
          { key: 'scope', value: 'One scope', label: 'Equipment, labor, and logistics' },
          { key: 'coverage', value: 'Florida', label: 'Miami, Orlando, and statewide projects' },
          { key: 'support', value: 'Show-ready', label: 'Install, test, support, and dismantle' },
          { key: 'formats', value: 'Booth + floor', label: 'Exhibitors, activations, and organizers' }
        ],
        paths: {
          eyebrow: 'Choose the right service path',
          title: 'Managed trade show AV or trade show equipment rental?',
          description: 'This page is for exhibitors, agencies, booth builders, and show organizers who need equipment and technical execution coordinated as one show-floor scope.',
          managed: {
            label: 'Managed trade show production',
            title: 'Need us to own the AV scope?',
            description: 'Send the show dates, venue, booth number, floor plan, exhibitor manual, and desired experience. We will define the system, labor, access plan, testing, support, and dismantle requirements.',
            action: 'Request a Trade Show AV Proposal'
          },
          equipment: {
            label: 'Specific equipment only',
            title: 'Already know the exact display you need?',
            description: 'Use the specialist rental pages when the job is limited to a specific LED wall, TV, or projector. Managed booth coordination stays with the trade show production team.',
            action: 'Compare LED Video Walls'
          }
        },
        formats: {
          eyebrow: 'Built for the show floor',
          title: 'Trade show booth AV for exhibitors, activations, and expo programs',
          description: 'The booth objective, show rules, content, sightlines, access windows, and operating plan determine the technical scope.',
          items: [
            { title: 'Exhibitor booths', description: 'Exhibitor AV services can include integrated displays, playback, sound, lighting, mounting, cable management, installation, testing, and dismantle.' },
            { title: 'Brand activations', description: 'High-impact visual moments, product demonstrations, audience engagement, and technical operation designed around the activation.' },
            { title: 'Sponsor displays', description: 'LED, TVs, digital signage, branded content, and reliable playback for sponsor and partner areas.' },
            { title: 'Product demonstrations', description: 'Displays, microphones, audio playback, switching, confidence screens, lighting, and technician support for scheduled demos.' },
            { title: 'Show-floor presentation areas', description: 'Compact stages, presentation displays, speech reinforcement, lighting, playback, and operator coverage.' },
            { title: 'Organizer & multi-booth programs', description: 'Exhibition AV services for organizers can combine repeatable systems, exhibitor coordination, schedules, floor-wide technical support, and one accountable production contact.' }
          ]
        },
        gallery: {
          eyebrow: 'Authentic trade show work',
          title: 'Real booth displays, activations, and show-floor systems',
          description: 'These are Miami Sound Rental trade show installations—not stock booth concepts. Each system was built around a different exhibitor, footprint, content plan, and venue environment.',
          items: [
            {
              src: '/Pictures/trade-show/louisiana-exhibit-led-wall.webp',
              alt: 'Wide LED video wall built into a Louisiana tourism exhibit at a trade show',
              title: 'Large-format booth LED',
              description: 'A wide, integrated LED display creates the primary visual surface for a destination-marketing exhibit.'
            },
            {
              src: '/Pictures/trade-show/alpha-sync-booth-displays.webp',
              alt: 'Alpha Sync trade show booth with a center LED wall and two presentation displays',
              title: 'Multi-display product demonstration',
              description: 'A central LED wall and supporting displays create separate content surfaces within a compact booth footprint.'
            },
            {
              src: '/Pictures/trade-show/hankook-ion-brand-activation.webp',
              alt: 'Hankook iON outdoor trade show activation with truss, lighting, and display systems',
              title: 'Outdoor sponsor activation',
              description: 'Truss, lighting, digital displays, and guest flow support a public-facing brand experience.'
            },
            {
              src: '/Pictures/trade-show/itic-americas-digital-agenda.webp',
              alt: 'Vertical digital agenda display at the ITIC Americas event',
              title: 'Digital agenda & wayfinding',
              description: 'A portrait display keeps live program information visible at an attendee arrival and networking area.'
            },
            {
              src: '/Pictures/trade-show/marina-expo-booth-led-wall.webp',
              alt: 'Large portrait LED video wall installed at a marina expo booth',
              title: 'Portrait LED feature',
              description: 'A tall LED canvas creates a visible booth landmark while preserving usable floor space.'
            },
            {
              src: '/Pictures/trade-show/fenty-beauty-exhibit-led-wall.webp',
              alt: 'Fenty Beauty trade show LED wall during installation with production cases nearby',
              title: 'Installation before show open',
              description: 'The finished visual surface depends on the less-visible work: delivery, assembly, signal, testing, and a disciplined install window.'
            }
          ]
        },
        capabilities: {
          eyebrow: 'One accountable AV scope',
          title: 'Technical systems coordinated around the exhibit',
          description: 'Trade show audio visual systems work best as one coordinated scope. MSR can combine equipment, crew, show documents, installation, content testing, and support instead of leaving the exhibitor to coordinate disconnected rentals.',
          items: [
            { icon: 'video', title: 'LED walls & displays', description: 'LED video walls, TVs, digital signage, confidence displays, mounting, processing, and signal distribution.' },
            { icon: 'audio', title: 'Booth audio', description: 'Speech, demos, playback, microphones, compact reinforcement, and controlled coverage suited to a shared show floor.' },
            { icon: 'lighting', title: 'Exhibit lighting', description: 'Product, presenter, scenic, accent, and camera-conscious lighting coordinated with the booth design.' },
            { icon: 'content', title: 'Playback & switching', description: 'Content review, playback systems, switching, aspect-ratio checks, signal paths, and pre-show testing.' },
            { icon: 'crew', title: 'Installation & show support', description: 'Delivery, assembly, cable management, commissioning, technician coverage, and organized dismantle.' },
            { icon: 'coordination', title: 'Show-floor coordination', description: 'Exhibitor manuals, access windows, booth builders, general contractors, electrical orders, internet, and venue requirements.' }
          ]
        },
        coordination: {
          eyebrow: 'Advance the booth before move-in',
          title: 'The AV plan has to fit the show rules—not only the booth rendering.',
          description: 'Trade show production succeeds when the technical design and the operational plan are advanced together. Share the exhibitor manual, floor plan, booth number, target experience, content, and any general-contractor or venue contacts as early as possible.',
          image: '/Pictures/trade-show/louisiana-booth-led-display.webp',
          imageAlt: 'Side view of a custom Louisiana tourism booth with an integrated LED display',
          items: [
            'Move-in, target-ready, show, and dismantle windows',
            'Booth dimensions, sightlines, mounting, and weight constraints',
            'Electrical, internet, rigging, and show-service orders',
            'Booth builder, decorator, general contractor, and venue coordination',
            'Content resolution, aspect ratio, playback, and testing requirements',
            'On-site technician coverage and escalation contacts'
          ]
        },
        process: {
          eyebrow: 'From brief through dismantle',
          title: 'A trade show production process built around fixed deadlines',
          description: 'The booth has one opening time. Each step is designed to surface requirements early and leave time for testing before attendees arrive.',
          steps: [
            { title: 'Share the show brief', description: 'Send the venue, dates, booth number, floor plan, exhibitor manual, content goals, and known partners.' },
            { title: 'Advance the requirements', description: 'We define equipment, labor, placement, power, signal, show-service orders, access, and technical dependencies.' },
            { title: 'Install, commission & test', description: 'The team loads in, assembles the system, confirms content and signal, and completes testing before the agreed ready time.' },
            { title: 'Support & dismantle', description: 'Technicians support the show as scoped, then power down, dismantle, and collect equipment within the event schedule.' }
          ]
        },
        faqs: [
          { question: 'What is included in managed trade show AV services?', answer: 'A managed scope can include system design, equipment, delivery, installation, cable management, content and signal testing, show-floor technical support, dismantle, and coordination with the exhibitor, agency, booth builder, general contractor, venue, and show rules. The exact scope is defined in the proposal.' },
          { question: 'Can you work from an exhibitor manual and booth floor plan?', answer: 'Yes. The exhibitor manual, booth number, floor plan, elevations, move-in windows, target-ready time, electrical plan, internet requirements, and contractor contacts help us identify technical dependencies before move-in.' },
          { question: 'Do you provide booth fabrication or exhibit construction?', answer: 'Miami Sound Rental focuses on the AV and technical-production scope. We coordinate with your exhibit house, booth builder, decorator, agency, general contractor, and venue so displays, lighting, audio, mounting, power, signal, and labor work with the booth design.' },
          { question: 'Can I rent only a video wall, TV, or projector?', answer: 'Yes. If you only need a specific display and already have the booth logistics handled, use the relevant LED video wall, TV, or projector rental page. Use this trade show service when the equipment, installation, content, crew, and show-floor requirements need to be coordinated together.' },
          { question: 'Can you support multiple booths or an organizer program?', answer: 'Yes. We can review repeatable exhibitor packages, sponsor areas, presentation zones, and multi-booth programs. The proposal should identify the number of spaces, service standards, show schedule, ordering process, and on-site support expectations.' },
          { question: 'How early should we request a trade show AV proposal?', answer: 'Contact us as soon as the venue, show dates, and booth footprint are known. Larger LED systems, custom mounting, multiple booths, show-service orders, and tight move-in schedules benefit from more advance time, but our team can also evaluate time-sensitive requests.' },
          { question: 'Which Florida trade show markets do you serve?', answer: 'Miami Sound Rental supports trade show and expo projects in Miami, Fort Lauderdale, Orlando, and other Florida markets. The final scope accounts for the venue, move-in schedule, crew travel, equipment, and show requirements.' },
          { question: 'What should I send with a proposal request?', answer: 'Send the show name, venue, dates, booth number, floor plan or rendering, exhibitor manual, target-ready time, show hours, dismantle window, display or audio goals, content status, and contacts for the booth builder, agency, venue, or general contractor when available.' }
        ],
        proposal: {
          eyebrow: 'Start the show-floor conversation',
          title: 'Request a trade show AV proposal',
          description: 'Share the information you already have. A producer will review the exhibit, show rules, schedule, content, technical scope, and coordination requirements.'
        },
        related: {
          eyebrow: 'Related services',
          title: 'Use the specialist path that matches the scope',
          items: [
            { title: 'Corporate Event Production', description: 'Conferences, conventions, general sessions, and multi-room corporate programs.', href: '/corporate-event-production' },
            { title: 'LED Video Wall Rental', description: 'Compare LED video wall formats when the requirement is primarily a display rental.', href: '/led-video-wall-rental' },
            { title: 'TV Rental', description: 'Individual displays and simple multi-screen presentation requirements.', href: '/tv-rental' },
            { title: 'Projector Rental', description: 'Projection systems for meetings, presentations, and controlled-light environments.', href: '/projector-rental' }
          ]
        }
      }
    }
  },
  changeSummary: 'Created a dedicated managed trade show and expo AV service page with authentic project proof, logistics guidance, and specialist routing.',
  changeReason: 'Trade show service intent was previously diluted between Corporate Event Production and equipment pages, leaving no page dedicated to booth AV coordination, installation, support, and dismantle.',
  hypothesis: 'A focused trade show AV page will improve qualified exhibitor and agency discovery while routing equipment-only demand to the appropriate rental pages and preserving broad corporate-production ownership.',
  primaryKpi: 'trade_show_proposal_requests'
}

export const tradeShowProductionFallback = {
  page: tradeShowProductionPage,
  revision: tradeShowProductionRevision,
  source: 'source-fallback'
}
